"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { FaArrowRight } from "react-icons/fa";

//  Composant réutilisable ProgressCircle avec transition de taille
const ProgressCircle = ({
  progress,
  isActive,
  onClick,
}: {
  progress: number;
  isActive: boolean;
  onClick: () => void;
}) => {
  return (
    <motion.svg
      width={isActive ? 20 : 20}
      height={isActive ? 20 : 20}
      viewBox="0 0 40 40"
      onClick={onClick}
      style={{ cursor: "pointer" }}
      animate={{ scale: isActive ? 1.2 : 1 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      {isActive ? (
        <>
          <circle
            cx="20"
            cy="20"
            r="18"
            stroke="#ccc"
            strokeWidth="4"
            fill="none"
          />
          <motion.circle
            cx="20"
            cy="20"
            r="18"
            stroke="#435b7b"
            strokeWidth="4"
            fill="none"
            strokeDasharray="113"
            strokeDashoffset={113 - progress * 113}
            initial={{ strokeDashoffset: 113 }}
            animate={{ strokeDashoffset: 113 - progress * 113 }}
            transition={{ duration: 0.2, ease: "linear" }}
          />
        </>
      ) : (
        <motion.circle
          cx="20"
          cy="20"
          r="6"
          fill="#ccc"
          animate={{ scale: 1 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        />
      )}
    </motion.svg>
  );
};

// Interface pour les vidéos
interface VideoItem {
  id: number;
  src: string;
  title: string;
  subject: string;
  discoverText: string;
  link: string;
}

interface VideoCarouselProps {
  videos: VideoItem[];
}

const VideoCarousel: React.FC<VideoCarouselProps> = ({ videos }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [direction, setDirection] = useState<"left" | "right">("left");
  const videoRef = useRef<HTMLVideoElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const animationFrameRef = useRef<number | null>(null);

  // ✅ Sélectionner une vidéo spécifique
  const handleSelectVideo = (index: number) => {
    setDirection(index > currentIndex ? "left" : "right");
    setCurrentIndex(index);
    setProgress(0);
  };

  const handleNext = useCallback(() => {
    setDirection("left");
    setCurrentIndex((prev) => (prev + 1) % videos.length);
    setProgress(0);
  }, [videos.length]);

  const handlePrev = useCallback(() => {
    setDirection("right");
    setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length);
    setProgress(0);
  }, [videos.length]);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    isDragging.current = true;
    startX.current = "touches" in e ? e.touches[0].clientX : e.clientX;
  };

  const handleDragEnd = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging.current) return;
    const endX =
      "changedTouches" in e ? e.changedTouches[0].clientX : e.clientX;
    const diff = startX.current - endX;

    if (diff > 50) handleNext();
    else if (diff < -50) handlePrev();

    isDragging.current = false;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Vérifier si la page est visible avant de lire la vidéo
    if (document.visibilityState === "visible") {
      video.play().catch((error) => console.error("Video play error:", error));
    }

    const updateProgress = () => {
      if (video.duration) {
        setProgress(video.currentTime / video.duration);
      }
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    };

    animationFrameRef.current = requestAnimationFrame(updateProgress);

    const handleEnded = () => handleNext();
    video.addEventListener("ended", handleEnded);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      video.removeEventListener("ended", handleEnded);
    };
  }, [currentIndex, handleNext]);

  return (
    <div
      className="video-carousel-container"
      onMouseDown={handleDragStart}
      onMouseUp={handleDragEnd}
      onTouchStart={handleDragStart}
      onTouchEnd={handleDragEnd}
    >
      {/* Animation fluide du changement de vidéo */}
      <motion.div
        key={videos[currentIndex].id}
        initial={{ x: direction === "left" ? "100%" : "-100%", opacity: 0 }}
        animate={{ x: "0%", opacity: 1 }}
        exit={{ x: direction === "left" ? "-100%" : "100%", opacity: 0 }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        className="video-wrapper"
      >
        <video
          ref={videoRef}
          src={videos[currentIndex].src}
          muted
          playsInline
          preload="auto"
        ></video>
      </motion.div>

      <div className="video-overlay">
        <motion.a
          href={videos[currentIndex].link}
          rel="noopener noreferrer"
          className="video-info"
          key={videos[currentIndex].id}
          initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -20, filter: "blur(5px)" }}
          transition={{
            duration: 0.8,
            ease: "easeOut",
            delay: 0.3,
          }}
        >
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
          >
            {videos[currentIndex].subject}
          </motion.h3>

          <motion.h1
            initial={{ opacity: 0, y: 20, letterSpacing: "-0.05em" }}
            animate={{ opacity: 1, y: 0, letterSpacing: "0em" }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.2 }}
          >
            {videos[currentIndex].title}
          </motion.h1>

          <motion.p
            className="discover-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
          >
            {videos[currentIndex].discoverText} <FaArrowRight />
          </motion.p>
        </motion.a>
      </div>

      <div className="carousel-dots">
        {videos.map((video, index) => (
          <ProgressCircle
            key={video.id}
            progress={index === currentIndex ? progress : 0}
            isActive={index === currentIndex}
            onClick={() => handleSelectVideo(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default VideoCarousel;
